import { useState, useEffect, useCallback } from "react";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuditLog } from "@/hooks/useAuditLog";

interface ViewAsTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sqlQuery: string;
  title: string;
}

/**
 * Transform a widget SQL query for "view as table":
 * - Replace the SELECT column list with SELECT *
 * - Remove GROUP BY clause
 * - Remove HAVING clause
 * - Remove ORDER BY (may reference removed aliases/aggregates)
 * - Apply a LIMIT for safety
 */
function transformSqlForTableView(sql: string): string {
  let q = sql.trim().replace(/;\s*$/, "");

  // Replace SELECT ... FROM with SELECT * FROM (outermost only).
  // We track parentheses depth so subqueries are left intact.
  const selectMatch = q.match(/^\s*(WITH\b[\s\S]*?\)\s*)?SELECT\b/i);
  if (selectMatch) {
    const prefix = selectMatch[1] || ""; // CTE prefix if any
    const afterCte = q.slice(prefix.length);

    // Find the FROM keyword at depth 0
    let depth = 0;
    let fromIdx = -1;
    const upper = afterCte.toUpperCase();
    for (let i = 0; i < upper.length; i++) {
      if (afterCte[i] === "(") depth++;
      else if (afterCte[i] === ")") depth--;
      else if (
        depth === 0 &&
        upper.slice(i, i + 5) === "FROM " &&
        (i === 0 || /\s/.test(afterCte[i - 1]))
      ) {
        fromIdx = i;
        break;
      }
    }
    if (fromIdx !== -1) {
      q = prefix + "SELECT * " + afterCte.slice(fromIdx);
    }
  }

  // Remove GROUP BY at depth 0
  q = removeClauseAtDepthZero(q, "GROUP BY");

  // Remove HAVING at depth 0
  q = removeClauseAtDepthZero(q, "HAVING");

  // Remove ORDER BY at depth 0 (may reference removed aggregates/aliases)
  q = removeClauseAtDepthZero(q, "ORDER BY");

  // Remove existing LIMIT (we'll add our own)
  q = removeClauseAtDepthZero(q, "LIMIT");

  // Apply a safe limit
  q = q.trim() + " LIMIT 100";

  return q;
}

/** Build a COUNT(*) query from the transformed SQL (without the LIMIT). */
function buildCountQuery(sql: string): string {
  let q = sql.trim().replace(/;\s*$/, "");

  const selectMatch = q.match(/^\s*(WITH\b[\s\S]*?\)\s*)?SELECT\b/i);
  if (selectMatch) {
    const prefix = selectMatch[1] || "";
    const afterCte = q.slice(prefix.length);
    let depth = 0;
    let fromIdx = -1;
    const upper = afterCte.toUpperCase();
    for (let i = 0; i < upper.length; i++) {
      if (afterCte[i] === "(") depth++;
      else if (afterCte[i] === ")") depth--;
      else if (
        depth === 0 &&
        upper.slice(i, i + 5) === "FROM " &&
        (i === 0 || /\s/.test(afterCte[i - 1]))
      ) {
        fromIdx = i;
        break;
      }
    }
    if (fromIdx !== -1) {
      q = prefix + "SELECT * " + afterCte.slice(fromIdx);
    }
  }

  q = removeClauseAtDepthZero(q, "GROUP BY");
  q = removeClauseAtDepthZero(q, "HAVING");
  q = removeClauseAtDepthZero(q, "ORDER BY");
  q = removeClauseAtDepthZero(q, "LIMIT");

  return `SELECT COUNT(*) AS total_count FROM (${q.trim()}) AS _count_subquery`;
}

/**
 * Remove a SQL clause (e.g. "GROUP BY", "ORDER BY") at parentheses depth 0.
 * Stops at the next depth-0 clause keyword or end of string.
 */
function removeClauseAtDepthZero(sql: string, clause: string): string {
  const upper = sql.toUpperCase();
  const clauseWords = clause.split(" ");
  let depth = 0;

  for (let i = 0; i < upper.length; i++) {
    if (sql[i] === "(") depth++;
    else if (sql[i] === ")") depth--;
    if (depth !== 0) continue;

    // Check if clause starts here
    const remaining = upper.slice(i);
    const pattern = clauseWords.join("\\s+");
    const re = new RegExp(`^${pattern}\\b`, "i");
    if (!re.test(remaining)) continue;

    // Make sure it's preceded by whitespace or start of string
    if (i > 0 && !/\s/.test(sql[i - 1])) continue;

    // Find where this clause's content ends: next depth-0 keyword or end
    const nextKeywords = ["SELECT", "FROM", "WHERE", "GROUP BY", "HAVING", "ORDER BY", "LIMIT", "UNION", "INTERSECT", "EXCEPT"];
    let endIdx = sql.length;
    let d = 0;
    for (let j = i + clause.length; j < sql.length; j++) {
      if (sql[j] === "(") d++;
      else if (sql[j] === ")") {
        if (d === 0) { endIdx = j; break; }
        d--;
      }
      if (d !== 0) continue;

      for (const kw of nextKeywords) {
        if (kw === clause) continue; // skip self
        const kwUpper = upper.slice(j, j + kw.length + 1);
        const kwRe = new RegExp(`^${kw.replace(" ", "\\s+")}\\b`, "i");
        if (kwRe.test(kwUpper) && (j === 0 || /\s/.test(sql[j - 1]))) {
          endIdx = j;
          break;
        }
      }
      if (endIdx !== sql.length) break;
    }

    return sql.slice(0, i) + sql.slice(endIdx);
  }

  return sql;
}

/** Parse a raw execute/queries response into row objects. */
function parseQueryRows(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (Array.isArray(first)) return first as Record<string, unknown>[];
    if (first && typeof first === "object" && ("rows" in first || "result" in first)) {
      return ((first as any).rows || (first as any).result || []) as Record<string, unknown>[];
    }
    return raw as Record<string, unknown>[];
  }
  if (raw && typeof raw === "object") {
    const r = raw as any;
    if (r.results) {
      const first = Array.isArray(r.results) ? r.results[0] : r.results;
      return Array.isArray(first) ? first : (first?.rows || first?.result || []);
    }
    if (r.rows) return r.rows;
  }
  return [];
}

const ViewAsTableDialog = ({ open, onOpenChange, sqlQuery, title }: ViewAsTableDialogProps) => {
  const { toast } = useToast();
  const { log: auditLog } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const executeQuery = useCallback(async () => {
    if (!sqlQuery) return;
    setLoading(true);
    setError(null);
    setColumns([]);
    setRows([]);
    setTotalCount(null);

    try {
      const transformedSql = transformSqlForTableView(sqlQuery);
      const countSql = buildCountQuery(sqlQuery);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
        method: "POST",
        headers,
        body: JSON.stringify([transformedSql, countSql]),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: "Query execution failed" }));
        throw new Error(errData.error || `Request failed with status ${res.status}`);
      }

      const responseData = await res.json();
      const resultsArray: unknown[] = Array.isArray(responseData) ? responseData : [responseData];

      // Parse data rows from first query
      const parsedRows = parseQueryRows(resultsArray[0]);

      // Parse total count from second query
      const countRows = parseQueryRows(resultsArray[1]);
      if (countRows.length > 0) {
        const countVal = countRows[0]["total_count"] ?? countRows[0][Object.keys(countRows[0])[0]];
        if (countVal != null) setTotalCount(Number(countVal));
      }

      if (parsedRows.length === 0) {
        setError("No data returned");
        return;
      }

      const cols = Object.keys(parsedRows[0]);
      setColumns(cols);
      setRows(parsedRows.map((r) => cols.map((c) => r[c] != null ? String(r[c]) : "")));
    } catch (err: any) {
      console.error("View as table error:", err);
      setError(err.message || "Failed to execute query");
    } finally {
      setLoading(false);
    }
  }, [sqlQuery]);

  useEffect(() => {
    if (open && sqlQuery) {
      executeQuery();
    }
    if (!open) {
      setColumns([]);
      setRows([]);
      setTotalCount(null);
      setError(null);
    }
  }, [open, sqlQuery, executeQuery]);

  const handleDownload = () => {
    if (columns.length === 0 || rows.length === 0) return;

    const escapeCsvField = (field: string) => {
      if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const csv = [
      columns.map(escapeCsvField).join(","),
      ...rows.map((row) => row.map(escapeCsvField).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title || "table-data"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: "Table data exported as CSV" });

    auditLog({
      action: "downloaded",
      resourceType: "dashboard",
      resourceName: title || "table-data",
      metadata: {
        format: "csv",
        row_count: rows.length,
        total_count: totalCount,
        column_count: columns.length,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>{title || "Table View"}</DialogTitle>
            {columns.length > 0 && rows.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="mr-2 h-3.5 w-3.5" />
                Download CSV
              </Button>
            )}
          </div>
          <DialogDescription>
            {loading
              ? "Running query…"
              : rows.length > 0
                ? totalCount != null
                  ? `${rows.length} records of total ${totalCount.toLocaleString()} records`
                  : `${rows.length} record${rows.length !== 1 ? "s" : ""}`
                : error
                  ? ""
                  : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto min-h-0">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-16 text-sm text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && columns.length > 0 && (
            <div className="overflow-auto max-h-[60vh] border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr className="border-b border-border">
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/50">
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 whitespace-nowrap max-w-xs truncate" title={cell}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewAsTableDialog;
